import {
  CANTEEN_ID,
  CANTEEN_SCHOOL_ID,
  CANTEEN_TENANT_ID,
  CanteenAccount,
  CanteenAuditLog,
  CanteenBlockedProduct,
  CanteenCashierMovement,
  CanteenCashierSession,
  CanteenCategory,
  CanteenClassGroup,
  CanteenDietaryRestriction,
  CanteenGuardian,
  CanteenMenuSlot,
  CanteenNotice,
  CanteenOrder,
  CanteenProduct,
  CanteenProfile,
  CanteenPromotion,
  CanteenSchool,
  CanteenStockMovement,
  CanteenStudent,
  CanteenTransaction,
  CanteenUnit,
  CanteenWeekday,
} from 'core/models/canteen.model';

const SCHOOL_DAYS: readonly CanteenWeekday[] = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
];

function atHour(dayOffset: number, hours: number, minutes = 0): string {
  const date = new Date();
  date.setDate(date.getDate() + dayOffset);
  date.setHours(hours, minutes, 0, 0);
  return date.toISOString();
}

const scope = {
  tenantId: CANTEEN_TENANT_ID,
  schoolId: CANTEEN_SCHOOL_ID,
  canteenId: CANTEEN_ID,
};

export const CANTEEN_SCHOOL: CanteenSchool = {
  id: CANTEEN_SCHOOL_ID,
  tenantId: CANTEEN_TENANT_ID,
  name: 'Escola Municipal Centro',
};

export const CANTEEN_UNIT: CanteenUnit = {
  id: CANTEEN_ID,
  tenantId: CANTEEN_TENANT_ID,
  schoolId: CANTEEN_SCHOOL_ID,
  name: 'Cantina Centro',
  description: 'Alimentação do turno regular.',
  status: 'ACTIVE',
  opensAt: '07:00',
  closesAt: '18:00',
  allowsSale: true,
  allowsScheduling: true,
  allowsBalance: true,
  allowsCounterSale: true,
  allowsPickup: true,
  allowsStudentOrders: true,
  minimumSchedulingAdvanceHours: 12,
  cancellationWindowHours: 2,
};

export const CANTEEN_CLASSES: readonly CanteenClassGroup[] = [
  { id: 'class-6a', schoolId: CANTEEN_SCHOOL_ID, name: '6º ano A' },
  { id: 'class-7b', schoolId: CANTEEN_SCHOOL_ID, name: '7º ano B' },
];

export const CANTEEN_STUDENTS: readonly CanteenStudent[] = [
  {
    id: 'stu-joao',
    schoolId: CANTEEN_SCHOOL_ID,
    enrollmentId: 'enr-joao',
    classId: 'class-6a',
    name: 'João Ribeiro',
    status: 'ACTIVE',
  },
  {
    id: 'stu-maria',
    schoolId: CANTEEN_SCHOOL_ID,
    enrollmentId: 'enr-maria',
    classId: 'class-6a',
    name: 'Maria Ribeiro',
    status: 'ACTIVE',
  },
  {
    id: 'stu-lucas',
    schoolId: CANTEEN_SCHOOL_ID,
    enrollmentId: 'enr-lucas',
    classId: 'class-7b',
    name: 'Lucas Almeida',
    status: 'ACTIVE',
  },
  {
    id: 'stu-pedro',
    schoolId: CANTEEN_SCHOOL_ID,
    enrollmentId: 'enr-pedro',
    classId: 'class-7b',
    name: 'Pedro Lima',
    status: 'INACTIVE',
  },
  {
    id: 'stu-sofia',
    schoolId: CANTEEN_SCHOOL_ID,
    enrollmentId: 'enr-sofia',
    classId: 'class-6a',
    name: 'Sofia Martins',
    status: 'ACTIVE',
  },
];

export const CANTEEN_GUARDIANS: readonly CanteenGuardian[] = [
  {
    id: 'grd-ana',
    name: 'Ana Ribeiro',
    studentIds: ['stu-joao', 'stu-maria'],
  },
  {
    id: 'grd-carlos',
    name: 'Carlos Almeida',
    studentIds: ['stu-lucas'],
  },
];

export const CANTEEN_PROFILES: readonly CanteenProfile[] = [
  {
    id: 'usr-admin',
    name: 'Helena Costa',
    role: 'CANTEEN_ADMIN',
    guardianId: null,
    studentId: null,
  },
  {
    id: 'usr-manager',
    name: 'Paulo Mendes',
    role: 'CANTEEN_MANAGER',
    guardianId: null,
    studentId: null,
  },
  {
    id: 'usr-operator',
    name: 'Rita Souza',
    role: 'CANTEEN_OPERATOR',
    guardianId: null,
    studentId: null,
  },
  {
    id: 'usr-ana',
    name: 'Ana Ribeiro',
    role: 'GUARDIAN',
    guardianId: 'grd-ana',
    studentId: null,
  },
  {
    id: 'usr-joao',
    name: 'João Ribeiro',
    role: 'STUDENT',
    guardianId: null,
    studentId: 'stu-joao',
  },
];

export const CANTEEN_ACCOUNTS: readonly CanteenAccount[] = [
  {
    id: 'acc-joao',
    ...scope,
    studentId: 'stu-joao',
    balance: 100,
    dailyLimit: 20,
    weeklyLimit: 80,
    monthlyLimit: 200,
    orderLimit: null,
    status: 'ACTIVE',
  },
  {
    id: 'acc-maria',
    ...scope,
    studentId: 'stu-maria',
    balance: 79,
    dailyLimit: 40,
    weeklyLimit: 120,
    monthlyLimit: 300,
    orderLimit: 25,
    status: 'ACTIVE',
  },
  {
    id: 'acc-lucas',
    ...scope,
    studentId: 'stu-lucas',
    balance: 5,
    dailyLimit: 50,
    weeklyLimit: null,
    monthlyLimit: null,
    orderLimit: null,
    status: 'ACTIVE',
  },
  {
    id: 'acc-pedro',
    ...scope,
    studentId: 'stu-pedro',
    balance: 10,
    dailyLimit: null,
    weeklyLimit: null,
    monthlyLimit: null,
    orderLimit: null,
    status: 'ACTIVE',
  },
  {
    id: 'acc-sofia',
    ...scope,
    studentId: 'stu-sofia',
    balance: 30,
    dailyLimit: null,
    weeklyLimit: null,
    monthlyLimit: null,
    orderLimit: null,
    status: 'BLOCKED',
  },
];

export const CANTEEN_CATEGORIES: readonly CanteenCategory[] = [
  { id: 'cat-drinks', ...scope, name: 'Bebidas', active: true },
  { id: 'cat-snacks', ...scope, name: 'Lanches', active: true },
  { id: 'cat-fruit', ...scope, name: 'Frutas', active: true },
  { id: 'cat-meals', ...scope, name: 'Refeições', active: true },
  { id: 'cat-sweets', ...scope, name: 'Doces', active: true },
  { id: 'cat-healthy', ...scope, name: 'Saudáveis', active: true },
  { id: 'cat-combos', ...scope, name: 'Combos', active: true },
];

function product(
  partial: Pick<
    CanteenProduct,
    | 'id'
    | 'categoryId'
    | 'name'
    | 'description'
    | 'price'
    | 'cost'
    | 'code'
    | 'stock'
    | 'minStock'
    | 'ingredients'
    | 'allergens'
    | 'nutrition'
  > &
    Partial<Pick<CanteenProduct, 'stockControlled' | 'availableForScheduling'>>,
): CanteenProduct {
  return {
    ...scope,
    unit: 'un',
    stockControlled: partial.stockControlled ?? true,
    active: true,
    availableForSale: true,
    availableForScheduling: partial.availableForScheduling ?? true,
    ...partial,
    imageUrl: `/canteen-products/${partial.id}.jpg`,
  };
}

export const CANTEEN_PRODUCTS: readonly CanteenProduct[] = [
  product({
    id: 'prd-water',
    categoryId: 'cat-drinks',
    name: 'Água',
    description: 'Garrafa 300 ml.',
    price: 3,
    cost: 1,
    code: 'AGU-300',
    stock: 40,
    minStock: 10,
    ingredients: [],
    allergens: [],
    nutrition: 'Sem calorias relevantes.',
  }),
  product({
    id: 'prd-juice',
    categoryId: 'cat-drinks',
    name: 'Suco',
    description: 'Suco de laranja 200 ml.',
    price: 5,
    cost: 2,
    code: 'SUC-200',
    stock: 25,
    minStock: 8,
    ingredients: ['laranja'],
    allergens: [],
    nutrition: 'Energia aproximada: 90 kcal.',
  }),
  product({
    id: 'prd-sandwich',
    categoryId: 'cat-snacks',
    name: 'Sanduíche Natural',
    description: 'Pão integral, frango e folhas.',
    price: 8,
    cost: 4,
    code: 'SAN-NAT',
    stock: 15,
    minStock: 5,
    ingredients: ['pão', 'frango', 'alface'],
    allergens: ['glúten'],
    nutrition: 'Energia aproximada: 280 kcal.',
  }),
  product({
    id: 'prd-snack',
    categoryId: 'cat-snacks',
    name: 'Salgado',
    description: 'Assado do dia.',
    price: 6,
    cost: 2.5,
    code: 'SAL-001',
    stock: 10,
    minStock: 4,
    ingredients: ['massa', 'queijo'],
    allergens: ['glúten', 'lactose'],
    nutrition: 'Energia aproximada: 220 kcal.',
  }),
  product({
    id: 'prd-fruit',
    categoryId: 'cat-fruit',
    name: 'Fruta',
    description: 'Fruta da estação.',
    price: 3,
    cost: 1.2,
    code: 'FRU-001',
    stock: 18,
    minStock: 6,
    ingredients: [],
    allergens: [],
    nutrition: 'Fonte de fibras.',
  }),
  product({
    id: 'prd-meal',
    categoryId: 'cat-meals',
    name: 'Refeição',
    description: 'Prato do dia.',
    price: 12,
    cost: 6,
    code: 'REF-DIA',
    stock: 20,
    minStock: 5,
    ingredients: ['arroz', 'feijão', 'legumes'],
    allergens: [],
    nutrition: 'Refeição completa.',
  }),
  product({
    id: 'prd-cake',
    categoryId: 'cat-sweets',
    name: 'Bolo',
    description: 'Fatia de bolo simples.',
    price: 5,
    cost: 2,
    code: 'BOL-001',
    stock: 8,
    minStock: 3,
    ingredients: ['farinha', 'ovo', 'açúcar'],
    allergens: ['glúten', 'ovo'],
    nutrition: 'Sobremesa.',
  }),
  product({
    id: 'prd-yogurt',
    categoryId: 'cat-healthy',
    name: 'Iogurte',
    description: 'Iogurte natural.',
    price: 4,
    cost: 1.8,
    code: 'IOG-001',
    stock: 12,
    minStock: 4,
    ingredients: ['leite', 'lactose'],
    allergens: ['lactose'],
    nutrition: 'Fonte de cálcio.',
  }),
  product({
    id: 'prd-combo',
    categoryId: 'cat-combos',
    name: 'Combo Recreio',
    description: 'Sanduíche, suco e fruta.',
    price: 15,
    cost: 7,
    code: 'CMB-REC',
    stock: 10,
    minStock: 3,
    ingredients: ['pão', 'frango', 'laranja'],
    allergens: ['glúten'],
    nutrition: 'Combo do recreio.',
  }),
  product({
    id: 'prd-cookie',
    categoryId: 'cat-sweets',
    name: 'Biscoito',
    description: 'Pacote individual.',
    price: 4,
    cost: 1.5,
    code: 'BIS-001',
    stock: 0,
    minStock: 5,
    ingredients: ['farinha', 'açúcar'],
    allergens: ['glúten'],
    nutrition: 'Biscoito doce.',
  }),
];

function menuSlot(
  id: string,
  productId: string,
  featured: boolean,
  startsAt: string,
  endsAt: string,
): CanteenMenuSlot {
  return {
    id,
    ...scope,
    productId,
    featured,
    weekdays: SCHOOL_DAYS,
    startsAt,
    endsAt,
    validFrom: '2026-01-01',
    validUntil: null,
    availableForScheduling: true,
    price: null,
  };
}

export const CANTEEN_MENU: readonly CanteenMenuSlot[] = [
  menuSlot('menu-water', 'prd-water', false, '07:00', '18:00'),
  menuSlot('menu-juice', 'prd-juice', true, '07:00', '18:00'),
  menuSlot('menu-sandwich', 'prd-sandwich', true, '07:00', '18:00'),
  menuSlot('menu-snack', 'prd-snack', false, '07:00', '18:00'),
  menuSlot('menu-fruit', 'prd-fruit', true, '07:00', '18:00'),
  menuSlot('menu-meal', 'prd-meal', false, '07:00', '15:00'),
  menuSlot('menu-cake', 'prd-cake', false, '07:00', '18:00'),
  menuSlot('menu-yogurt', 'prd-yogurt', false, '07:00', '18:00'),
  menuSlot('menu-combo', 'prd-combo', true, '07:00', '18:00'),
  menuSlot('menu-cookie', 'prd-cookie', false, '07:00', '18:00'),
];

export const CANTEEN_PROMOTIONS: readonly CanteenPromotion[] = [
  {
    id: 'promo-cake',
    ...scope,
    name: 'Bolo da semana',
    kind: 'PERCENT',
    productId: 'prd-cake',
    value: 10,
    validFrom: '2026-01-01',
    validUntil: null,
    active: true,
  },
];

export const CANTEEN_BLOCKS: readonly CanteenBlockedProduct[] = [
  { studentId: 'stu-joao', productId: 'prd-cookie' },
];

export const CANTEEN_RESTRICTIONS: readonly CanteenDietaryRestriction[] = [
  { id: 'rst-maria', studentId: 'stu-maria', ingredient: 'lactose' },
];

export const CANTEEN_ORDERS: readonly CanteenOrder[] = [
  {
    id: 'ord-121',
    number: '000121',
    ...scope,
    studentId: 'stu-maria',
    guardianId: 'grd-ana',
    items: [
      {
        productId: 'prd-sandwich',
        productName: 'Sanduíche Natural',
        quantity: 1,
        unitPrice: 8,
        total: 8,
      },
      {
        productId: 'prd-juice',
        productName: 'Suco',
        quantity: 1,
        unitPrice: 5,
        total: 5,
      },
    ],
    subtotal: 13,
    discount: 0,
    total: 13,
    paymentMethod: 'BALANCE',
    channel: 'APP',
    createdAt: atHour(0, 8, 10),
    scheduledFor: null,
    status: 'READY_FOR_PICKUP',
    qrCode: 'MOCK-ORDER-000121',
    pickedUpBy: null,
    pickedUpAt: null,
    operatorId: null,
  },
  {
    id: 'ord-122',
    number: '000122',
    ...scope,
    studentId: 'stu-joao',
    guardianId: 'grd-ana',
    items: [
      {
        productId: 'prd-meal',
        productName: 'Refeição',
        quantity: 1,
        unitPrice: 12,
        total: 12,
      },
      {
        productId: 'prd-juice',
        productName: 'Suco',
        quantity: 1,
        unitPrice: 6,
        total: 6,
      },
    ],
    subtotal: 18,
    discount: 0,
    total: 18,
    paymentMethod: 'BALANCE',
    channel: 'POS',
    createdAt: atHour(0, 7, 20),
    scheduledFor: null,
    status: 'PAID',
    qrCode: 'MOCK-ORDER-000122',
    pickedUpBy: null,
    pickedUpAt: null,
    operatorId: 'usr-operator',
  },
  {
    id: 'ord-123',
    number: '000123',
    ...scope,
    studentId: 'stu-maria',
    guardianId: 'grd-ana',
    items: [
      {
        productId: 'prd-fruit',
        productName: 'Fruta',
        quantity: 1,
        unitPrice: 3,
        total: 3,
      },
      {
        productId: 'prd-juice',
        productName: 'Suco',
        quantity: 1,
        unitPrice: 5,
        total: 5,
      },
    ],
    subtotal: 8,
    discount: 0,
    total: 8,
    paymentMethod: 'BALANCE',
    channel: 'APP',
    createdAt: atHour(0, 7, 5),
    scheduledFor: atHour(1, 10, 0),
    status: 'SCHEDULED',
    qrCode: 'MOCK-ORDER-000123',
    pickedUpBy: null,
    pickedUpAt: null,
    operatorId: null,
  },
  {
    id: 'ord-124',
    number: '000124',
    ...scope,
    studentId: 'stu-sofia',
    guardianId: null,
    items: [
      {
        productId: 'prd-combo',
        productName: 'Combo Recreio',
        quantity: 1,
        unitPrice: 15,
        total: 15,
      },
    ],
    subtotal: 15,
    discount: 0,
    total: 15,
    paymentMethod: 'BALANCE',
    channel: 'POS',
    createdAt: atHour(-1, 10, 0),
    scheduledFor: null,
    status: 'REFUNDED',
    qrCode: 'MOCK-ORDER-000124',
    pickedUpBy: null,
    pickedUpAt: null,
    operatorId: 'usr-operator',
  },
  {
    id: 'ord-125',
    number: '000125',
    ...scope,
    studentId: 'stu-lucas',
    guardianId: 'grd-carlos',
    items: [
      {
        productId: 'prd-water',
        productName: 'Água',
        quantity: 1,
        unitPrice: 3,
        total: 3,
      },
    ],
    subtotal: 3,
    discount: 0,
    total: 3,
    paymentMethod: 'CASH',
    channel: 'POS',
    createdAt: atHour(-2, 9, 30),
    scheduledFor: null,
    status: 'DELIVERED',
    qrCode: 'MOCK-ORDER-000125',
    pickedUpBy: 'Lucas Almeida',
    pickedUpAt: atHour(-2, 9, 40),
    operatorId: 'usr-operator',
  },
];

export const CANTEEN_TRANSACTIONS: readonly CanteenTransaction[] = [
  {
    id: 'txn-joao-debit',
    ...scope,
    accountId: 'acc-joao',
    studentId: 'stu-joao',
    type: 'DEBIT',
    amount: 18,
    balanceAfter: 100,
    description: 'Compra - Refeição e suco',
    occurredAt: atHour(0, 7, 20),
    orderId: 'ord-122',
  },
  {
    id: 'txn-maria-pickup',
    ...scope,
    accountId: 'acc-maria',
    studentId: 'stu-maria',
    type: 'DEBIT',
    amount: 13,
    balanceAfter: 87,
    description: 'Compra - Sanduíche Natural e suco',
    occurredAt: atHour(0, 8, 10),
    orderId: 'ord-121',
  },
  {
    id: 'txn-maria-schedule',
    ...scope,
    accountId: 'acc-maria',
    studentId: 'stu-maria',
    type: 'DEBIT',
    amount: 8,
    balanceAfter: 79,
    description: 'Agendamento - Fruta e suco',
    occurredAt: atHour(0, 7, 5),
    orderId: 'ord-123',
  },
  {
    id: 'txn-sofia-debit',
    ...scope,
    accountId: 'acc-sofia',
    studentId: 'stu-sofia',
    type: 'DEBIT',
    amount: 15,
    balanceAfter: 15,
    description: 'Compra - Combo Recreio',
    occurredAt: atHour(-1, 10, 0),
    orderId: 'ord-124',
  },
  {
    id: 'txn-sofia-refund',
    ...scope,
    accountId: 'acc-sofia',
    studentId: 'stu-sofia',
    type: 'REFUND',
    amount: 15,
    balanceAfter: 30,
    description: 'Devolução - Combo Recreio',
    occurredAt: atHour(-1, 11, 0),
    orderId: 'ord-124',
  },
];

export const CANTEEN_STOCK_MOVEMENTS: readonly CanteenStockMovement[] = [
  {
    id: 'stk-1',
    ...scope,
    productId: 'prd-meal',
    type: 'SALE',
    quantity: 1,
    occurredAt: atHour(0, 7, 20),
    note: 'Pedido 000122',
  },
  {
    id: 'stk-2',
    ...scope,
    productId: 'prd-cookie',
    type: 'LOSS',
    quantity: 2,
    occurredAt: atHour(-3, 16, 0),
    note: 'Quebra no estoque',
  },
];

export const CANTEEN_CASHIER_SESSION: CanteenCashierSession = {
  id: 'cash-open',
  ...scope,
  status: 'OPEN',
  openedAt: atHour(0, 7, 0),
  closedAt: null,
  openingBalance: 100,
  openedBy: 'usr-manager',
};

export const CANTEEN_CASHIER_MOVEMENTS: readonly CanteenCashierMovement[] = [
  {
    id: 'cm-open',
    sessionId: 'cash-open',
    type: 'OPENING',
    amount: 100,
    occurredAt: atHour(0, 7, 0),
    note: 'Abertura do dia',
  },
  {
    id: 'cm-sale',
    sessionId: 'cash-open',
    type: 'SALE',
    amount: 18,
    occurredAt: atHour(0, 7, 20),
    note: 'Pedido 000122',
  },
];

export const CANTEEN_AUDIT: readonly CanteenAuditLog[] = [
  {
    id: 'aud-1',
    tenantId: CANTEEN_TENANT_ID,
    schoolId: CANTEEN_SCHOOL_ID,
    userId: 'usr-operator',
    action: 'ORDER_CREATED',
    entity: 'order',
    entityId: 'ord-122',
    timestamp: atHour(0, 7, 20),
    description: 'Pedido 000122 registrado no PDV.',
  },
  {
    id: 'aud-2',
    tenantId: CANTEEN_TENANT_ID,
    schoolId: CANTEEN_SCHOOL_ID,
    userId: 'usr-manager',
    action: 'CASHIER_OPENED',
    entity: 'cashier',
    entityId: 'cash-open',
    timestamp: atHour(0, 7, 0),
    description: 'Caixa aberto com saldo inicial de R$ 100,00.',
  },
  {
    id: 'aud-3',
    tenantId: CANTEEN_TENANT_ID,
    schoolId: CANTEEN_SCHOOL_ID,
    userId: 'usr-manager',
    action: 'REFUND_CREATED',
    entity: 'order',
    entityId: 'ord-124',
    timestamp: atHour(-1, 11, 0),
    description: 'Devolução do pedido 000124.',
  },
];

export const CANTEEN_NOTICES: readonly CanteenNotice[] = [
  {
    id: 'ntc-1',
    title: 'Cardápio da semana',
    body: 'Combo Recreio disponível de segunda a sexta.',
    createdAt: atHour(0, 7, 0),
    role: 'ALL',
  },
  {
    id: 'ntc-2',
    title: 'Retirada',
    body: 'Pedido 000121 pronto para retirada.',
    createdAt: atHour(0, 8, 40),
    role: 'GUARDIAN',
  },
];
